import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { driverMethodCollectingRepository } from "../data/driverMethodCollectingRepository";
import { getAvailableMethodCollectingUseCase } from "../domain/getAvailableMethodCollectingUseCase";
import { createDriverMethodCollectingUseCase } from "../domain/createDriverMethodCollectingUseCase";

export function AddDriverMethodCollectingModal({
  driver,
  assignedMethods,
  onClose,
  onSaved,
}) {
  const [options, setOptions] = useState([]);
  const [methodId, setMethodId] = useState("");
  const [paymentValue, setPaymentValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setLoadError(null);
    setMethodId("");

    async function loadOptions() {
      const result = await getAvailableMethodCollectingUseCase(
        driverMethodCollectingRepository,
        assignedMethods,
      );
      if (!active) return;
      setOptions(result.data);
      setLoadError(result.error);
      setIsLoading(false);
    }
    loadOptions();
    return () => {
      active = false;
    };
  }, [assignedMethods, attempt]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting.current || isLoading || loadError) return;
    submitting.current = true;
    setIsSaving(true);
    setSaveError(null);
    const method = options.find((option) => String(option.id) === String(methodId));
    const result = await createDriverMethodCollectingUseCase(
      driverMethodCollectingRepository,
      driver.id,
      method,
      paymentValue,
    );
    submitting.current = false;
    setIsSaving(false);
    if (result.success) {
      onSaved(result.data);
    } else {
      setSaveError(result.error);
    }
  }

  return (
    <Dialog
      open
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="add-driver-method-collecting-title"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle id="add-driver-method-collecting-title">
          Agregar método de cobro
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress
                color="secondary"
                aria-label="Cargando opciones de cobro"
              />
            </Box>
          ) : loadError ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  onClick={() => setAttempt((value) => value + 1)}
                >
                  Reintentar
                </Button>
              }
            >
              {loadError}
            </Alert>
          ) : options.length === 0 ? (
            <Typography py={2}>
              No hay métodos de cobro disponibles para agregar.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                select
                fullWidth
                required
                label="Método de cobro"
                value={methodId}
                disabled={isSaving}
                onChange={(event) => setMethodId(event.target.value)}
              >
                {options.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Información de cobro"
                value={paymentValue}
                disabled={isSaving}
                inputProps={{ maxLength: 255 }}
                helperText="Opcional. Máximo 255 caracteres."
                onChange={(event) => setPaymentValue(event.target.value)}
              />
              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={isLoading || isSaving || !!loadError || !methodId || options.length === 0}
          >
            {isSaving ? "Guardando..." : "Aceptar"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
